'use client';

import { createSPAClient } from '@/lib/supabase/client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Eye, EyeOff } from 'lucide-react';
import SSOButtons from '@/components/SSOButtons';
import { captureClientError } from '@/lib/error-reporting/client';

/**
 * Map raw Supabase auth errors to user-friendly messages.
 * Supabase returns generic errors that confuse users — especially "Invalid login credentials"
 * which doesn't tell them if it's the email, password, or unverified account.
 */
function getFriendlyAuthError(error: Error): string {
  const msg = error.message?.toLowerCase() || '';

  if (msg.includes('invalid login credentials') || msg.includes('invalid_credentials')) {
    return 'Wrong email or password. If you were invited to a team, use the invitation link from your email. You can also reset your password below.';
  }
  if (msg.includes('email not confirmed') || msg.includes('email_not_confirmed')) {
    return 'Your email has not been verified yet. Check your inbox for a verification link, or sign up again to receive a new one.';
  }
  if (msg.includes('too many requests') || msg.includes('rate_limit')) {
    return 'Too many sign-in attempts. Please wait a moment and try again.';
  }
  if (msg.includes('user not found') || msg.includes('no user found')) {
    return 'No account found with this email. Please check the address or create a new account.';
  }
  if (msg.includes('user is banned') || msg.includes('user_banned')) {
    return 'This account has been suspended. Please contact support if you believe this is a mistake.';
  }

  return error.message;
}

/** Expected auth errors that should NOT be reported to Sentry (user mistakes / admin actions, not bugs) */
function isExpectedAuthError(error: Error): boolean {
  const msg = error.message?.toLowerCase() || '';
  return (
    msg.includes('invalid login credentials') ||
    msg.includes('invalid_credentials') ||
    msg.includes('email not confirmed') ||
    msg.includes('email_not_confirmed') ||
    msg.includes('too many requests') ||
    msg.includes('rate_limit') ||
    msg.includes('user not found') ||
    msg.includes('no user found') ||
    msg.includes('user is banned') ||
    msg.includes('user_banned')
  );
}

interface LoginFormProps {
  /** Pre-fill the email field (e.g., after invite signup) */
  initialEmail?: string;
  /** Error from auth callback redirect (e.g., expired link, failed OAuth) */
  initialError?: string;
  /** Destination path to redirect after successful login (e.g., invitation acceptance) */
  initialRedirect?: string;
}

export function LoginForm({ initialEmail, initialError, initialRedirect }: LoginFormProps) {
  const [email, setEmail] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(initialError || '');
  const [loading, setLoading] = useState(false);
  const [showMFAPrompt, setShowMFAPrompt] = useState(false);
  const router = useRouter();

  // Support redirect parameter (e.g., from invitation flow) — passed as prop from server page
  const redirectTo = initialRedirect;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const supabase = createSPAClient();
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

      // Handle sign-in errors: expected errors (wrong password, etc.) show a message
      // without reporting to Sentry. Unexpected errors still get reported.
      if (signInError) {
        if (isExpectedAuthError(signInError)) {
          setError(getFriendlyAuthError(signInError));
          return;
        }
        throw signInError;
      }

      // Check if MFA is required
      const { data: mfaData, error: mfaError } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

      if (mfaError) throw mfaError;

      if (mfaData.nextLevel === 'aal2' && mfaData.nextLevel !== mfaData.currentLevel) {
        setShowMFAPrompt(true);
      } else {
        // Redirect to original destination or dashboard
        // Validate redirect path against allowlist to prevent open redirect attacks
        const SAFE_REDIRECT_PREFIXES = [
          '/dashboard',
          '/onboarding',
          '/auth/team/accept-invitation/',
        ];
        const isSafeRedirect =
          redirectTo && SAFE_REDIRECT_PREFIXES.some((prefix) => redirectTo.startsWith(prefix));
        router.push(isSafeRedirect ? redirectTo : '/dashboard');
        return;
      }
    } catch (err) {
      // Only unexpected errors reach here — report to Sentry
      captureClientError(err, 'client_runtime', {
        component: 'LoginForm',
        action: 'signInWithPassword',
      }).catch(() => {});
      if (err instanceof Error) {
        setError(getFriendlyAuthError(err));
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (showMFAPrompt) {
      router.push('/auth/2fa');
    }
  }, [showMFAPrompt, router]);

  return (
    <>
      {error && (
        <div className="p-4 text-sm text-red-300 bg-red-900/20 border border-red-500/30 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full appearance-none rounded-lg border border-yellow-500/30 bg-black/50 px-4 py-3 pr-12 text-white placeholder-gray-500 shadow-sm focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 transition-all"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-200 transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <Link
              href="/auth/forgot-password"
              className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors"
            >
              Forgot your password?
            </Link>
          </div>
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className="group relative w-full flex justify-center rounded-lg border border-transparent bg-gradient-to-r from-yellow-400 to-amber-600 py-3 px-4 text-sm font-bold text-black shadow-lg hover:from-yellow-500 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </div>
      </form>

      <SSOButtons onError={setError} />

      <div className="text-center text-sm border-t border-yellow-500/20 pt-6">
        <span className="text-gray-400">Don&apos;t have an account?</span>{' '}
        <Link
          href="/auth/register"
          className="font-medium text-yellow-500 hover:text-yellow-400 transition-colors"
        >
          Sign up
        </Link>
      </div>
    </>
  );
}
